import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelizeKeys } from 'src/utils/decamelize-keys';

import {
  associationsToId,
  associationsToIds,
  dateToTimestamp,
  removeReadOnlyFields,
  timestampToDate,
} from '../../utils/fieldNormalizers';

import {
  MaxVisitDay,
  ScheduledAppointment,
  UpdateScheduledAppointmentParams,
} from './_types';

const denormalizeMaxVisitDays = (
  maxVisitDays: MaxVisitDay[]
): Array<{ day: number; max_visits: number | null }> =>
  maxVisitDays.map((entry) => ({
    day: entry.id,
    max_visits: entry.maxVisits ?? null,
  }));

const readOnlyFields = [
  'created_at',
  'updated_at',
  'platform_id',
  'links',
  // Read-only associations
  'service',
  'location',
  'primary_trainers',
  'secondary_trainers',
  'resources',
];

export function normalizeScheduledAppointment(
  scheduledAppointment: Record<string, unknown>
): ScheduledAppointment {
  const camelized = camelcaseKeys(scheduledAppointment, { deep: true });

  return {
    ...camelized,
    startTime: timestampToDate(camelized.startTime),
    endTime: timestampToDate(camelized.endTime),
  };
}

export function denormalizeScheduledAppointment(
  scheduledAppointment: Partial<ScheduledAppointment>
): Record<string, unknown> {
  let decamelized = decamelizeKeys(scheduledAppointment, { deep: true });

  decamelized = associationsToId(decamelized, ['service', 'location']);
  decamelized = associationsToIds(decamelized, [
    'primary_trainers',
    'secondary_trainers',
  ]);

  removeReadOnlyFields(decamelized, readOnlyFields);

  const result: Record<string, unknown> = {
    ...decamelized,
  };

  // Convert Date fields to Unix timestamps
  if (decamelized.start_time !== undefined) {
    result.start_time = dateToTimestamp(decamelized.start_time);
  }
  if (decamelized.end_time !== undefined) {
    result.end_time = dateToTimestamp(decamelized.end_time);
  }

  // Handle serviceResourceRules explicitly to ensure correct field names
  // This matches denormalizeUpdateParams behavior
  if (scheduledAppointment.serviceResourceRules !== undefined) {
    result.service_resource_rules = scheduledAppointment.serviceResourceRules.map(
      (sr) => ({
        resource_id: sr.resourceId,
        usage_behavior: sr.usageBehavior,
        sub_resource_ids: sr.subResourceIds || [],
      })
    );
  }

  if (scheduledAppointment.maxVisitDays !== undefined) {
    result.max_visit_days = denormalizeMaxVisitDays(
      scheduledAppointment.maxVisitDays
    );
  }

  return result;
}

/**
 * Denormalize update params for the scheduled appointment API
 * This creates the payload for PUT /api/v4/fbm/scheduled_appointments/:id
 * Matches v2 modal-new-appointment.js saveScheduledAppointment flow
 */
export function denormalizeUpdateParams(
  params: UpdateScheduledAppointmentParams
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  if (params.serviceId !== undefined) {
    payload.service_id = params.serviceId;
  }
  if (params.locationId !== undefined) {
    payload.location_id = params.locationId;
  }
  if (params.primaryTrainerIds !== undefined) {
    payload.primary_trainer_ids = params.primaryTrainerIds;
  }
  if (params.secondaryTrainerIds !== undefined) {
    payload.secondary_trainer_ids = params.secondaryTrainerIds;
  }
  if (params.startTime !== undefined) {
    payload.start_time = dateToTimestamp(params.startTime);
  }
  if (params.endTime !== undefined) {
    payload.end_time = dateToTimestamp(params.endTime);
  }
  if (params.recurringType !== undefined) {
    payload.recurring_type = params.recurringType;
  }
  if (params.recurringCount !== undefined) {
    payload.recurring_count = params.recurringCount;
  }
  if (params.days !== undefined) {
    payload.days = params.days;
  }
  if (params.recurringDayNumbers !== undefined) {
    payload.recurring_day_numbers = params.recurringDayNumbers;
  }
  if (params.endAt !== undefined) {
    payload.end_at = params.endAt;
  }
  if (params.recurringType !== undefined) {
    payload.recurring_type = params.recurringType;
  }
  if (params.recurringCount !== undefined) {
    payload.recurring_count = params.recurringCount;
  }
  if (params.recurringDayNumbers !== undefined) {
    payload.recurring_day_numbers = params.recurringDayNumbers;
  }
  if (params.days !== undefined) {
    payload.days = params.days;
  }
  if (params.bookRecurring !== undefined) {
    payload.book_recurring = params.bookRecurring;
  }
  if (params.description !== undefined) {
    payload.description = params.description;
  }
  if (params.notes !== undefined) {
    payload.notes = params.notes;
  }
  if (params.noResources !== undefined) {
    payload.no_resources = params.noResources;
  }
  if (params.disableEmail !== undefined) {
    payload.disable_email = params.disableEmail;
  }
  if (params.zoom !== undefined) {
    payload.zoom = params.zoom
      ? decamelizeKeys(params.zoom, { deep: true })
      : null;
  }
  if (params.maxVisits !== undefined) {
    payload.max_visits = params.maxVisits;
  }
  if (params.maxVisitDays !== undefined) {
    payload.max_visit_days = denormalizeMaxVisitDays(params.maxVisitDays);
  }
  if (params.serviceResourceRules !== undefined) {
    // serviceResourceRules need to be denormalized manually
    // since they use different field names than the camelCase version
    payload.service_resource_rules = params.serviceResourceRules.map((sr) => ({
      resource_id: sr.resourceId,
      usage_behavior: sr.usageBehavior,
      sub_resource_ids: sr.subResourceIds || [],
    }));
  }

  return payload;
}

